import type { CaptureFileJSONObject } from '@ikegps/core-api';
import { SVGRenderer } from '@ikegps/core-shared';
import type { CollectionModel } from '@ikegps/core-shared';
import { addStyles } from '../../components/theme';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { Services } from '../../services';
import { classNames } from '../../util/classnames';
import { HiddenImageIcon } from '../hidden.image.icon/hidden.image.icon';
import { CSS, Style } from './capture.preview.style';

addStyles(Style);

export enum CapturePreviewType {
	THUMBNAIL = 'thumbnail',
	MEDIUM = 'medium',
	RAW = 'raw',
}

export interface CapturePreviewCtx {
	type: CapturePreviewType;
	crosshair?: boolean;
	truesize?: boolean;
	selected?: boolean;
	className?: string;
	size?: number;
	title?: string;
	icon?: boolean;
	withOverlay?: boolean;
	action?: (ev: MouseEvent) => void;
}

const DEFAULT_THUMBNAIL_SIZE = 180;

export const CapturePreview = {
	view(
		collection: CollectionModel,
		capture: CaptureFileJSONObject,
		ctx: CapturePreviewCtx = { type: CapturePreviewType.THUMBNAIL }
	): MithrilVNode {
		const draw = Services.DrawState.getIkeDraw(collection, capture.objectId);
		const className = classNames(CSS.container, ctx.className, {
			[CSS.selectable]: typeof ctx.action === 'function',
			[CSS.selected]: ctx.selected,
			[CSS.hiddenCapture]: draw.state.imageVisibilityHidden,
		});
		const title = ctx.title;
		const onclick = ctx.action;

		// Select image URL based on preview type
		let src: string;
		if (ctx.type === CapturePreviewType.THUMBNAIL && capture.thumbnail) {
			src = capture.thumbnail.url;
		} else if (ctx.type === CapturePreviewType.MEDIUM && capture.medium) {
			src = capture.medium.url;
		} else {
			src = capture.file.url;
		}

		const size = {
			width: DEFAULT_THUMBNAIL_SIZE,
			height: DEFAULT_THUMBNAIL_SIZE,
		};
		if (ctx.size) {
			size.width = ctx.size;
			size.height = ctx.size;
		}

		const renderCtx = {
			draw,
			...(ctx.type === CapturePreviewType.THUMBNAIL && { size }),
			units: Services.UserSettings.units,
			dateFormat: Services.UserSettings.dateFormat,

			isThumbnail: ctx.type === CapturePreviewType.THUMBNAIL,
			isInteractive: false,
			isEditable: false,
			scale: SVGRenderer.scaleUI(draw.state, ctx.type === CapturePreviewType.THUMBNAIL),
		};

		const hiddenIcon = draw.state.imageVisibilityHidden ? HiddenImageIcon.view() : null;
		const dateElement = m('div', { className: CSS.date }, Services.DateTime.formatDate(capture.collectedAt.iso));

		// NOTE: Brightness adjustment check was removed because it caused inconsistent behavior:
		// - When brightness was modified, the image showed annotations
		// - When brightness was not modified, annotations were hidden
		// - Now always show just the image
		if (!ctx.crosshair && !ctx.truesize) {
			const wrapperClass = classNames(CSS.wrapper, {
				[CSS.hiddenCapture]: draw.state.imageVisibilityHidden,
			});

			return m(
				'div',
				{ className: CSS.container, title, onclick },
				m('div', { className: wrapperClass }, [
					m('img', { className: CSS.image, src }),
					m('div', { className: CSS.overlay }, SVGRenderer.view(renderCtx)),
					hiddenIcon,
					dateElement,
				])
			);
		}

		if (ctx.truesize) {
			return m('div', { className, title, onclick }, [
				m('img', {
					className: CSS.image,
					src,
				}),
				m('div', { className: CSS.overlay }, SVGRenderer.view(renderCtx)),
				ctx.icon ? m('div', { className: CSS.photomeasure }) : null,
				hiddenIcon,
				dateElement,
			]);
		}

		return m('div', { className, title, onclick }, [
			m('img', {
				className: CSS.image,
				src,
			}),
			draw.state.brightness !== 1 ? m('div', { className: CSS.overlay }, SVGRenderer.view(renderCtx)) : null,
			ctx.crosshair ? m('div', { className: CSS.crosshair }) : null,
			hiddenIcon,
			dateElement,
		]);
	},
};
